import { styled, useMediaQuery } from "@mui/material"
import { LicenseInfo } from "@mui/x-license"
import { useQueryClient } from "@tanstack/react-query"
import { useFlags } from "launchdarkly-react-client-sdk"
import type React from "react"
import { useEffect, useRef } from "react"
import { Navigate, Outlet, useLocation } from "react-router"
import { MobileHeader } from "./components/AppHeader/MobileHeader"
import { CriticalThreatBanner } from "./components/Banners/CriticalThreatBanner"
import { StaleDataBanner } from "./components/Banners/StaleDataBanner"
import { CapServiceContextProvider } from "./components/CriticalThreats/capServiceContext"
import DrawerNav from "./components/DrawerNav"
import { TermsModal } from "./components/TermsModal"
import { TermsOfServiceModal } from "./components/TermsOfService/TermsOfServiceModal"
import { UserPreferencesProvider } from "./contexts/UserPreferencesContext"
import { useLDIdentify } from "./hooks/useLDIdentify"
import { usePermissions } from "./hooks/usePermissions"
import { useRouteAccess } from "./hooks/useRouteAccess"
import { useStoreInitialUrl } from "./hooks/useStoreInitialUrl"
import useAuth from "./utils/auth"
import { ROUTES, UNPROTECTED_ROUTES } from "./utils/constants"
import { useMixpanel } from "./utils/mixpanel"
import { extraColors, theme } from "./utils/theme"
import { useAxiosAuthInterceptor } from "./utils/useAxiosAuthInterceptor"
import { useChatModels } from "./components/Chat/hooks/useChatModelState"

const Layout = styled("div")({
  display: "grid",
  gridTemplateColumns: "min-content 1fr",
  gridTemplateRows: "min-content 1fr",
  gridTemplateAreas: `'nav banner'
                      'nav content'`,
  alignContent: "start",
  justifyContent: "start",
  width: "100%",
  height: "100%",
  zIndex: 2,
  borderTop: `1px solid ${extraColors.status.navy}`,
})

const Content = styled("div")({
  display: "grid",
  position: "relative",
})

const ContentGradient = styled("div")({
  background: `linear-gradient(270deg, ${extraColors.status.navySubtle} 0%, ${extraColors.status.navyLight} 31.73%, ${extraColors.status.navy} 70.19%, ${extraColors.status.navyDark} 100%)`,
  opacity: 0.8,
  height: 3,
  position: "absolute",
  zIndex: 1,
  width: "100%",
})

const LayoutContainer = styled("div")({
  background: `linear-gradient(180deg, ${extraColors.dark} 66%, black 100%)`,
  position: "relative",
  zIndex: 999,
  minHeight: "100vh",
  display: "grid",
  gridTemplateRows: "1fr",
  [theme.breakpoints.down("md")]: {
    gridTemplateRows: "min-content 1fr",
  },
})

/**
 * Auth gate that ensures the user is authenticated and LaunchDarkly
 * flags are loaded before rendering protected content.
 */
const RequireAuth: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading, error } = useAuth()
  const location = useLocation()
  const client = useQueryClient()
  const isUnprotectedRoute = UNPROTECTED_ROUTES.includes(location.pathname)
  const isLDReady = useLDIdentify()
  const wasAuthenticated = useRef(false)
  const { canAccessPath } = useRouteAccess()

  useEffect(() => {
    if (wasAuthenticated.current && !isAuthenticated) {
      client.clear()
    }
    wasAuthenticated.current = isAuthenticated
  }, [isAuthenticated, client])

  // Unprotected routes (sign-in, terms, etc.) render without auth or LD checks
  if (isUnprotectedRoute) {
    return <>{children}</>
  }

  if (isLoading || error) {
    return null
  }

  if (!isAuthenticated) {
    return (
      <Navigate
        to={{ pathname: ROUTES.SIGN_IN, search: location.search }}
        state={{ from: location }}
      />
    )
  }

  if (!isLDReady) {
    return null
  }

  if (!canAccessPath(location.pathname)) {
    return <Navigate to={{ pathname: ROUTES.PULSE }} replace />
  }

  return <>{children}</>
}

LicenseInfo.setLicenseKey(import.meta.env.MUI_LICENSE)

function App() {
  const isMobile = useMediaQuery(theme.breakpoints.down("md"))
  const { isLoading, error, isAuthenticated } = useAuth()

  const location = useLocation()
  const permissions = usePermissions()
  useMixpanel()
  // this hook captures page views across the application and sends the data to mixpanel

  useStoreInitialUrl()
  useAxiosAuthInterceptor()

  if (error && location.pathname !== ROUTES.ERROR) {
    return <Navigate to={{ pathname: ROUTES.ERROR, search: location.search }} />
  }

  if (isLoading || permissions.isLoading) {
    return null
  }

  return (
    <UserPreferencesProvider>
      <CapServiceContextProvider>
        <LayoutContainer>
          <RequireAuth>
            {isMobile && isAuthenticated && <MobileHeader />}
            <Layout>
              <ContentGradient data-testid="content-gradient" />
              <ProtectedLayoutComponents />
              <Content
                sx={{
                  gridTemplateRows: "auto 1fr",
                  gridArea: "content",
                }}
              >
                <Outlet />
              </Content>
            </Layout>
          </RequireAuth>
        </LayoutContainer>
      </CapServiceContextProvider>
    </UserPreferencesProvider>
  )
}

const ProtectedLayoutComponents: React.FC = () => {
  const isMobile = useMediaQuery(theme.breakpoints.down("md"))
  const { showTrialTermsOfService, allowNonBetaRoutes } = useFlags()
  const { isAuthenticated, user: authUser } = useAuth()

  useChatModels()

  const isFirstTimeUser =
    !!authUser?.["https://thepublichealthco.com/v1/is_first_login"]

  if (!isAuthenticated) return null

  return (
    <>
      {!isMobile && <DrawerNav />}
      {showTrialTermsOfService ? (
        <TermsOfServiceModal />
      ) : (
        <TermsModal isFirstTimeUser={isFirstTimeUser} />
      )}
      <div style={{ gridArea: "banner", alignContent: "start" }}>
        <StaleDataBanner />
        {allowNonBetaRoutes && <CriticalThreatBanner />}
      </div>
    </>
  )
}

export default App
